import {CmcCollectionsSynchronizer} from './cmc.collections.synchronizer';
import {CouchdbCollectionsSynchronizer} from './couchdb.collections.synchronizer';
import {OpportunityCollectionSynchronizer} from './opportunity.collection.synchronizer';
import {QuoteCollectionSynchronizer} from './quote.collection.synchronizer';
import {EQuoteOpportunityCollectionSynchronizer} from './equote-opportunity.collection.synchronizer';
import {Injectable} from '@angular/core';
import {
    SynchroniseCollectionModalComponent
} from '../../../components/standalone-opportunity-history/synchronise-collection-modal/synchronise-collection-modal.component';
import {DialogService} from 'primeng/dynamicdialog';
import {Store} from '@ngrx/store';
import {firstValueFrom} from 'rxjs';
import {getEmployee, selectStandaloneState} from '../../store';
import {CurrentCmcVersionAction, FirstTimeSyncDone, StandaloneState} from '../../store/standalone';
import {StandardsProductsSynchronizer} from "./standards-products.synchronizer";
import {ParametersSynchronizer} from "./parameters.synchronizer";
import {RxdbCmcProtocolRepository} from '../repositories/rxdb-cmc-protocol.repository';

@Injectable({
    providedIn: 'root'
})
export class EppSynchronizerService{

    private syncGeneration = 0;

    constructor(
        private couchdbCollectionsSynchronizer: CouchdbCollectionsSynchronizer,
        private cmcCollectionsSynchronizer: CmcCollectionsSynchronizer,
        private standardsProductsSynchronizer: StandardsProductsSynchronizer,
        private readonly parametersSynchronizer: ParametersSynchronizer,
        private opportunityCollectionSynchronizer: OpportunityCollectionSynchronizer,
        private quoteCollectionSynchronizer: QuoteCollectionSynchronizer,
        private eQuoteOpportunityCollectionSynchronizer: EQuoteOpportunityCollectionSynchronizer,
        private dialogService: DialogService,
        private store: Store<StandaloneState>,
        private rxdbCmcProtocolRepository: RxdbCmcProtocolRepository

    ) {

    }

    public async init(isCrmLinked: boolean) {
        const standaloneState: StandaloneState = await firstValueFrom(this.store.select(selectStandaloneState));
        const employee = await firstValueFrom(this.store.select(getEmployee));

        if(!standaloneState.isFirstTimeSyncDone || !standaloneState.currentCmcVersion || !standaloneState.currentStandardVersion) {
            this.dialogService.open(SynchroniseCollectionModalComponent, {
                closable: false,
                focusOnShow: false,
                dismissableMask: false
            });
        }
        if(isCrmLinked) {
            await this.quoteCollectionSynchronizer.startPush();
            await this.opportunityCollectionSynchronizer.pull(!standaloneState.isFirstTimeSyncDone);
            await this.quoteCollectionSynchronizer.pull(!standaloneState.isFirstTimeSyncDone, standaloneState.quoteCollectionCheckPoint);
        }else {
            await this.eQuoteOpportunityCollectionSynchronizer.startPush();
            await this.eQuoteOpportunityCollectionSynchronizer.pull(!standaloneState.isFirstTimeSyncDone);
        }
        await this.couchdbCollectionsSynchronizer.synchronizeCollections(standaloneState.isFirstTimeSyncDone, employee?.countryIsoCode.toLowerCase());
        const hasCmcProtocols = await this.rxdbCmcProtocolRepository.hasCmcProtocols(employee?.countryIsoCode);
        if (hasCmcProtocols) {
            await this.cmcCollectionsSynchronizer.init(employee?.countryIsoCode.toLowerCase());
        }else{
            this.store.dispatch(new CurrentCmcVersionAction(''));
        }
        await this.standardsProductsSynchronizer.init(employee?.countryIsoCode.toLowerCase());
        await this.parametersSynchronizer.init(employee?.countryIsoCode.toLowerCase());
        this.store.dispatch(new FirstTimeSyncDone());
    }

    public async launchSyncPeriodically(isCrmLinked: boolean) {
        const generation = ++this.syncGeneration;
        try {
            while (generation === this.syncGeneration) {
                const startTime = Date.now();
                const intervalMs = 60_000;

                this.checkAndLaunchSync(isCrmLinked);

                // attendre avant de relancer un nouveau cycle
                const elapsed = Date.now() - startTime;
                const waitTime = Math.max(0, intervalMs - elapsed);
                console.log(`Attente de ${waitTime / 1000}s avant la prochaine sync...`);
                await new Promise(r => setTimeout(r, waitTime));
            }
        } catch (err) {
            console.error('Erreur pendant la réplication séquentielle', err);
        }
    }


    public async launchSync(isCrmLinked: boolean) {
        const standaloneState: StandaloneState = await firstValueFrom(this.store.select(selectStandaloneState));
        const employee = await firstValueFrom(this.store.select(getEmployee));
        if(standaloneState.isFirstTimeSyncDone) {
            console.log('🔁 Relance de la synchro ponctuelle...');
            await this.couchdbCollectionsSynchronizer.synchronizeCollections(true, employee?.countryIsoCode.toLowerCase());
            if(isCrmLinked) {
                await this.quoteCollectionSynchronizer.pull(false, standaloneState.quoteCollectionCheckPoint);
            } else {
                await this.eQuoteOpportunityCollectionSynchronizer.pull(false);
            }
            const hasCmcProtocols = await this.rxdbCmcProtocolRepository.hasCmcProtocols(employee?.countryIsoCode);
            if (hasCmcProtocols) {
                await this.cmcCollectionsSynchronizer.synchronizeCmcProducts();
            } else {
                console.log('Aucun protocole CMC, synchronisation des produits CMC ignorée.');
            }
            await this.standardsProductsSynchronizer.synchronizeProducts();
            await this.parametersSynchronizer.synchronizeParameters();
        }
    }

    /**
     * Arrête le push et la boucle de synchro périodique de cet onglet (logout).
     */
    public stop(): void {
        this.syncGeneration++;
        this.quoteCollectionSynchronizer.stopPush();
        this.eQuoteOpportunityCollectionSynchronizer.stopPush();
    }

    /**
     * Pousse immédiatement les données offline en attente (devis CRM ou opportunités eQuote).
     */
    public async flushPendingPushes(): Promise<void> {
        const isCrmLinked = localStorage.getItem('isCrmLinked') === 'true';
        if (isCrmLinked) {
            await this.quoteCollectionSynchronizer.pushPending();
        } else {
            await this.eQuoteOpportunityCollectionSynchronizer.pushPending();
        }
    }

    public async checkAndLaunchSync(isCrmLinked: boolean) {
        const standaloneState: StandaloneState = await firstValueFrom(this.store.select(selectStandaloneState));
        const allDone = Object.values(standaloneState.liveCollectionsSyncStatus).every(task => task.status === "DONE");
        if (allDone) {
            this.launchSync(isCrmLinked);
        }
    }

}
